import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ShoppingBag, Search, Heart, Plus, Minus, X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../context/I18nContext";

const PRODUCTS = [
  { id: 1, name: "Cabas Élise", price: 290, color: "bg-rose-100", category: "Sacs" },
  { id: 2, name: "Sandales Margaux", price: 180, color: "bg-amber-100", category: "Chaussures" },
  { id: 3, name: "Foulard Aurélien", price: 95, color: "bg-orange-100", category: "Accessoires" },
  { id: 4, name: "Veste Camille", price: 420, color: "bg-stone-200", category: "Vêtements" },
  { id: 5, name: "Robe Hortense", price: 340, color: "bg-emerald-100", category: "Vêtements" },
  { id: 6, name: "Ceinture Léon", price: 75, color: "bg-yellow-100", category: "Accessoires" },
];

const Topbar = () => {
  const { t } = useI18n();
  return (
    <div className="fixed top-4 left-4 right-4 z-50 flex justify-between items-center pointer-events-none">
      <Link
        to="/#portfolio"
        data-testid="demo-back"
        className="pointer-events-auto inline-flex items-center gap-2 bg-zinc-950/80 backdrop-blur border border-white/10 px-3 py-2 text-xs text-zinc-200 hover:bg-zinc-900"
      >
        <ArrowLeft size={12} /> {t("demos.back")}
      </Link>
      <Link
        to="/simulateur"
        data-testid="demo-cta-quote"
        className="pointer-events-auto inline-flex items-center gap-2 bg-white text-zinc-950 px-3 py-2 text-xs hover:bg-zinc-200"
      >
        {t("demos.cta_quote")} <ArrowUpRight size={12} />
      </Link>
    </div>
  );
};

export default function ExempleEcommerce() {
  const [cart, setCart] = useState([]);
  const [open, setOpen] = useState(false);

  const add = (p) => {
    setCart((prev) => {
      const existing = prev.find((x) => x.id === p.id);
      if (existing) return prev.map((x) => (x.id === p.id ? { ...x, qty: x.qty + 1 } : x));
      return [...prev, { ...p, qty: 1 }];
    });
    toast.success(`${p.name} ajouté au panier`);
  };
  const inc = (id) => setCart((p) => p.map((x) => (x.id === id ? { ...x, qty: x.qty + 1 } : x)));
  const dec = (id) => setCart((p) => p.flatMap((x) => (x.id === id ? (x.qty > 1 ? [{ ...x, qty: x.qty - 1 }] : []) : [x])));
  const total = cart.reduce((s, x) => s + x.price * x.qty, 0);
  const count = cart.reduce((s, x) => s + x.qty, 0);

  return (
    <div className="min-h-screen bg-amber-50 text-stone-900" data-testid="demo-ecommerce">
      <Topbar />
      {/* Header */}
      <header className="px-8 py-6 flex items-center justify-between border-b border-stone-300/50 sticky top-0 bg-amber-50/95 backdrop-blur z-40">
        <p className="font-display text-2xl italic tracking-tighter">Atelier Bruyère</p>
        <nav className="hidden md:flex gap-8 text-xs uppercase tracking-[0.2em] text-stone-700">
          <a className="hover:text-stone-900" href="#shop">Boutique</a>
          <a className="hover:text-stone-900" href="#histoire">Histoire</a>
          <a className="hover:text-stone-900" href="#carnet">Carnet</a>
        </nav>
        <div className="flex items-center gap-3">
          <button data-testid="ecom-search" className="p-2 hover:bg-stone-200">
            <Search size={16} />
          </button>
          <button data-testid="ecom-cart-btn" onClick={() => setOpen(true)} className="relative p-2 hover:bg-stone-200">
            <ShoppingBag size={16} />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-stone-900 text-amber-50 text-[10px] font-mono w-5 h-5 flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="px-8 py-16 md:py-24 max-w-6xl mx-auto text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-6">— Collection Printemps 2026</p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[1.02]">
          Mode <em className="italic text-stone-600">artisanale</em>
          <br />
          fabriquée en France.
        </h1>
        <p className="mt-8 max-w-md mx-auto text-stone-600">
          Pièces faites main dans nos ateliers de Lyon. Petites séries, matières nobles, transparence totale.
        </p>
      </section>

      {/* Shop grid */}
      <section id="shop" className="px-8 pb-20 max-w-6xl mx-auto">
        <div className="flex items-end justify-between mb-8">
          <p className="text-xs uppercase tracking-[0.3em] text-stone-500">— {PRODUCTS.length} pièces</p>
          <select className="bg-transparent border border-stone-400 px-3 py-2 text-xs uppercase tracking-[0.15em]" defaultValue="">
            <option value="">Trier — Nouveautés</option>
            <option>Prix croissant</option>
            <option>Prix décroissant</option>
          </select>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-stone-300/50 border border-stone-300/50">
          {PRODUCTS.map((p) => (
            <article key={p.id} className="bg-amber-50 group">
              <div className={`relative aspect-square ${p.color} overflow-hidden`}>
                <button className="absolute top-3 right-3 w-8 h-8 bg-amber-50/80 backdrop-blur flex items-center justify-center hover:bg-amber-50">
                  <Heart size={14} />
                </button>
                <button
                  data-testid={`ecom-add-${p.id}`}
                  onClick={() => add(p)}
                  className="absolute bottom-0 left-0 right-0 bg-stone-900 text-amber-50 py-3 text-xs uppercase tracking-[0.2em] translate-y-full group-hover:translate-y-0 transition-transform"
                >
                  Ajouter au panier
                </button>
              </div>
              <div className="p-4 flex justify-between items-baseline">
                <div>
                  <p className="text-xs text-stone-500">{p.category}</p>
                  <p className="font-display text-lg">{p.name}</p>
                </div>
                <p className="font-mono text-sm">{p.price} €</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="px-8 py-10 border-t border-stone-300/50 text-xs text-stone-500 flex justify-between max-w-6xl mx-auto">
        <p>© 2025 Atelier Bruyère — Lyon, France</p>
        <p className="font-display italic">Démo Versant Studio</p>
      </footer>

      {/* Cart Drawer */}
      {open && (
        <div data-testid="ecom-cart-drawer" className="fixed inset-0 z-[60] flex">
          <div className="flex-1 bg-stone-900/40" onClick={() => setOpen(false)} />
          <aside className="w-full max-w-md bg-amber-50 h-full flex flex-col border-l border-stone-300/50">
            <div className="p-5 flex items-center justify-between border-b border-stone-300/50">
              <p className="font-display text-2xl italic">Panier</p>
              <button data-testid="ecom-cart-close" onClick={() => setOpen(false)} className="p-2 hover:bg-stone-200">
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {cart.length === 0 && <p className="text-sm text-stone-500">Votre panier est vide.</p>}
              {cart.map((item) => (
                <div key={item.id} className="flex items-center gap-4 border-b border-stone-300/30 pb-4">
                  <div className={`w-16 h-16 ${item.color}`} />
                  <div className="flex-1">
                    <p className="font-display text-base">{item.name}</p>
                    <p className="text-xs text-stone-500">{item.category}</p>
                    <div className="mt-2 inline-flex items-center border border-stone-400">
                      <button
                        data-testid={`ecom-dec-${item.id}`}
                        onClick={() => dec(item.id)}
                        className="p-1.5 hover:bg-stone-200"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="px-3 text-xs font-mono">{item.qty}</span>
                      <button
                        data-testid={`ecom-inc-${item.id}`}
                        onClick={() => inc(item.id)}
                        className="p-1.5 hover:bg-stone-200"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                  <p className="font-mono text-sm">{item.price * item.qty} €</p>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-stone-300/50">
              <div className="flex justify-between mb-4">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">Total</p>
                <p data-testid="ecom-cart-total" className="font-display text-2xl">{total} €</p>
              </div>
              <button
                disabled={cart.length === 0}
                data-testid="ecom-checkout"
                onClick={() => toast.info("Démo — checkout désactivé")}
                className="w-full bg-stone-900 text-amber-50 py-3 text-sm hover:bg-stone-800 disabled:opacity-40"
              >
                Passer commande
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
